// Project Data Configuration
// This file contains all project information
// Images and icons are imported from centralized asset files
// Metadata (dateCreated, commitCount) is imported from projectMetadata.json

import { ProjectImages } from "@/assets/images";
import projectMetadata from "./projectMetadata.json";
import {
  JavascriptIcon,
  ReactIcon,
  TypescriptIcon,
  CSSIcon,
  HTMLIcon,
  ZustandIcon,
  TailwindIcon,
  AxiosIcon,
  StorybookIcon,
  ShadCnIcon,
  VueIcon,
  QuasarIcon,
  PiniaIcon,
  VuetifyIcon,
  VueXIcon,
  ReduxIcon,
  FirebaseIcon,
  GoogleCloudIcon,
  RapidAPIIcon,
  RubyIcon,
  RailsIcon,
  SupabaseIcon,
  VercelIcon,
  HerokuIcon,
} from "@/assets/icons";

// Helper function to check if project is new (created within last 90 days)
const isProjectNew = (dateCreated) => {
  if (!dateCreated) return false;
  try {
    const createdDate = new Date(dateCreated);
    const now = new Date();
    const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
    return daysDiff <= 90;
  } catch (e) {
    return false;
  }
};

// Helper function to get project images
const getProjectImages = (category, key) => {
  const images = ProjectImages[category]?.[key];
  if (!images) {
    return {
      card: ProjectImages.mini.comingSoon,
      detail: [
        ProjectImages.mini.comingSoon,
        ProjectImages.mini.comingSoon,
        ProjectImages.mini.comingSoon,
      ],
    };
  }
  return images;
};

// Project data array
// To add a new project, simply add a new object with the required fields
const projectData = [
  {
    id: "Reva",
    title: "Reva",
    path: "reva",
    workRepo: true,
    type: "pro",
    stack: "Ruby",
    detailTitle: "Reva | Review Management & BI for Vacation Rentals",
    translationKey: "reva",
    imageKey: "reva",
    siteLink: "https://www.joinreva.com/",
    icons: [RubyIcon, RailsIcon, JavascriptIcon, TailwindIcon, HerokuIcon],
    cardIcons: [RubyIcon, RailsIcon],
  },
  {
    id: "LinkZap",
    title: "LinkZap",
    path: "linkzap",
    workRepo: false,
    type: "pro",
    stack: "TypeScript",
    detailTitle: "LinkZap | Link-in-Bio SaaS Platform",
    translationKey: "linkzap",
    imageKey: "linkzap",
    siteLink: "https://www.linkzap.click/",
    icons: [ReactIcon, TypescriptIcon, SupabaseIcon, VercelIcon, TailwindIcon],
  },
  {
    id: "CanalSmart",
    title: "CanalSmart",
    path: "canalsmart",
    workRepo: false,
    type: "pro",
    stack: "TypeScript",
    detailTitle: "CanalSmart | AI-Powered Real Estate WhatsApp Bot",
    translationKey: "canalsmart",
    imageKey: "canalsmart",
    siteLink: "https://real-estate-whatsapp-bot.vercel.app", // Update with your actual URL
    icons: [
      ReactIcon,
      TypescriptIcon,
      SupabaseIcon,
      VercelIcon,
      TailwindIcon,
      ShadCnIcon,
    ],
  },
  {
    id: "Mongoose Wizard",
    title: "Mongoose Wizard",
    path: "cesanta",
    workRepo: true,
    type: "pro",
    stack: "Javascript",
    detailTitle: "Mongoose Wizard (Cesanta) |  ",
    translationKey: "cesanta",
    imageKey: "cesanta",
    icons: [JavascriptIcon, TypescriptIcon, CSSIcon, HTMLIcon],
    cardIcons: [JavascriptIcon, CSSIcon],
  },
  {
    id: "Trainline Hub (Admin)",
    title: "Trainline Hub (Admin)",
    path: "trainline-admin",
    workRepo: true,
    type: "pro",
    stack: "React",
    detailTitle: "Trainline Hub (Admin) |  ",
    translationKey: "trainline-admin",
    imageKey: "trainlineAdmin",
    icons: [ReactIcon, TypescriptIcon, ZustandIcon, TailwindIcon, AxiosIcon],
  },
  {
    id: "Trainline UI Library",
    title: "Trainline UI Library",
    path: "trainline-library",
    workRepo: true,
    type: "pro",
    stack: "React",
    detailTitle: "UI Component Library | ",
    translationKey: "trainline-library",
    imageKey: "uiLibrary",
    extra: (
      <div>
        <a
          href="https://www.youtube.com/watch?v=Pw81e8DuU-A&ab_channel=RenatoJunior"
          target="_blank"
          rel="noopener noreferrer"
        >
          ShadCn Presentation
        </a>{" "}
        |{" "}
        <a
          href="https://docs.google.com/presentation/d/1jyPVHs5YsOWePyOJ2hA2UifA5ttt4Xg9LMF4dt6pSFs/edit#slide=id.g14d2792e9da_0_8"
          target="_blank"
          rel="noopener noreferrer"
        >
          ShadCn PowerPoint
        </a>
      </div>
    ),
    icons: [
      ReactIcon,
      TypescriptIcon,
      TailwindIcon,
      StorybookIcon,
      ShadCnIcon,
      ZustandIcon,
    ],
  },
  {
    id: "Trainline Hub (User)",
    title: "Trainline Hub (User)",
    path: "trainline-user",
    workRepo: true,
    type: "pro",
    stack: "React",
    detailTitle: "Trainline Hub (User) |  ",
    translationKey: "trainline-user",
    imageKey: "trainlineUser",
    icons: [ReactIcon, TypescriptIcon, TailwindIcon, ZustandIcon, AxiosIcon],
  },
  {
    id: "Budgeting Project",
    title: "Budgeting Project",
    path: "budgeting",
    workRepo: true,
    type: "pro",
    stack: "Vue",
    detailTitle: "Budgeting Project |  ",
    translationKey: "budgeting",
    imageKey: "jrProject",
    icons: [VueIcon, QuasarIcon, TypescriptIcon, PiniaIcon, CSSIcon, HTMLIcon],
  },
  {
    id: "Hairdresser Reservation (Admin)",
    title: "Hairdresser Reservation (Admin)",
    path: "reservation",
    workRepo: true,
    type: "pro",
    stack: "Vue",
    detailTitle: "Hairdresser Reservation System (Admin) |  ",
    translationKey: "reservation",
    imageKey: "svenson",
    icons: [VueIcon, TypescriptIcon, VuetifyIcon, VueXIcon, CSSIcon, HTMLIcon],
  },
  {
    id: "Hairdresser Reservation (User)",
    title: "Hairdresser Reservation (User)",
    path: "reservation-user",
    workRepo: true,
    type: "pro",
    stack: "Vue",
    detailTitle: "Hairdresser Reservation System (User) |  ",
    translationKey: "reservation-user",
    imageKey: "svensonUser",
    icons: [VueIcon, TypescriptIcon, VuetifyIcon, CSSIcon, HTMLIcon],
  },
  {
    id: "Nursery Management",
    title: "Nursery Management",
    path: "nursery",
    workRepo: true,
    type: "pro",
    stack: "Vue",
    detailTitle: "Nursery Data Management |  ",
    translationKey: "nursery",
    imageKey: "nursery",
    icons: [VueIcon, VuetifyIcon, VueXIcon, CSSIcon, HTMLIcon],
  },
  {
    id: "Kammeko",
    title: "Kammeko",
    path: "kammeko",
    workRepo: true,
    siteLink: "https://www.kammeko.com/",
    type: "pro",
    stack: "React",
    detailTitle: "Kammeko |  ",
    translationKey: "kammeko",
    imageKey: "kammeko",
    icons: [
      JavascriptIcon,
      TypescriptIcon,
      ReactIcon,
      ReduxIcon,
      CSSIcon,
      HTMLIcon,
    ],
  },
  {
    id: "Octacle",
    title: "Octacle",
    path: "octacle",
    workRepo: true,
    siteLink: "https://www.octacle.app/",
    type: "pro",
    stack: "Vue",
    detailTitle: "Octacle |  ",
    translationKey: "octacle",
    imageKey: "octacle",
    icons: [
      VueIcon,
      TailwindIcon,
      JavascriptIcon,
      TypescriptIcon,
      CSSIcon,
      HTMLIcon,
    ],
  },
  {
    id: "Budget Calculator",
    title: "Budget Calculator",
    path: "budget-calculator",
    siteLink: "https://bridgecodetest.netlify.app/",
    GhLink: "https://github.com/Netsujr/bridge-code-test",
    type: "pro",
    stack: "React",
    detailTitle: "Budget Calculator |  ",
    translationKey: "budget-calculator",
    imageKey: "budgetCalculator",
    icons: [
      ReactIcon,
      TailwindIcon,
      JavascriptIcon,
      TypescriptIcon,
      ZustandIcon,
    ],
  },
  {
    id: "nSuns Gym",
    title: "nSuns Gym",
    path: "nsuns",
    workRepo: true,
    siteLink: "https://netsunsuns.netlify.app/cycles",
    stack: "TypeScript",
    detailTitle: "nSuns Gym | Lifting, Cardio & Recovery Tracker",
    translationKey: "nsuns",
    imageKey: "nsuns",
    icons: [TypescriptIcon, TailwindIcon],
    cardIcons: [TypescriptIcon, TailwindIcon],
  },
  {
    id: "Streaky",
    title: "Streaky",
    siteLink: "https://streaky-rlam.onrender.com/",
    GhLink: "https://github.com/Netsujr/streaky",
    path: "streaky",
    offline: true,
    stack: "Ruby",
    detailTitle: "Streaky | Habit Tracker",
    translationKey: "streaky",
    imageKey: "streaky",
    icons: [RubyIcon, RailsIcon, TailwindIcon],
    cardIcons: [RubyIcon, RailsIcon],
  },
  {
    id: "Playwright E2E Testing Framework",
    title: "Playwright E2E Testing Framework",
    GhLink: "https://github.com/Netsujr/playwright-foundation-e2e",
    path: "playwright-e2e",
    offline: true,
    stack: "TypeScript",
    detailTitle: "Playwright E2E Testing Framework",
    translationKey: "playwright-e2e",
    imageKey: "playwright",
    icons: [TypescriptIcon],
    cardIcons: [TypescriptIcon],
    category: "tests",
    reportUrl: "https://netsujr.github.io/playwright-foundation-e2e/",
  },
  {
    id: "Playwright Advanced E2E Testing Framework",
    title: "Playwright Advanced E2E Testing Framework",
    GhLink: "https://github.com/Netsujr/playwright-advanced-e2e",
    path: "playwright-advanced-e2e",
    offline: true,
    stack: "TypeScript",
    detailTitle: "Playwright Advanced E2E Testing Framework",
    translationKey: "playwright-advanced-e2e",
    imageKey: "playwright",
    icons: [TypescriptIcon],
    cardIcons: [TypescriptIcon],
    category: "tests",
    reportUrl: "https://netsujr.github.io/playwright-advanced-e2e/",
  },
  {
    id: "CAIXA Lottery Tracker",
    title: "CAIXA Lottery Tracker",
    path: "lottery-tracker",
    stack: "TypeScript",
    detailTitle: "CAIXA Lottery Tracker | ",
    translationKey: "caixaLotteryTracker",
    imageKey: "lottery",
    siteLink: "https://caixa-lottery-tracker.onrender.com/",
    icons: [ReactIcon, TypescriptIcon, TailwindIcon],
    cardIcons: [ReactIcon, TypescriptIcon],
  },
  {
    id: "React Film List",
    title: "React Film List",
    siteLink: "https://renato-redux-filmlist.netlify.app/",
    GhLink: "https://github.com/Netsujr/Redux-Movie_List",
    path: "redux-film",
    stack: "React",
    detailTitle: "React MovieList |  ",
    translationKey: "redux-film",
    imageKey: "reduxFilm",
    icons: [ReactIcon, JavascriptIcon, HTMLIcon, CSSIcon, ReduxIcon],
  },
  {
    id: "CRUD-React & Redux",
    title: "CRUD-React & Redux",
    siteLink: "https://renato-redux-store.netlify.app/",
    GhLink: "https://github.com/Netsujr/redux_store",
    path: "redux-store",
    stack: "React",
    detailTitle: "React & Redux CRUD Store | ",
    translationKey: "redux-store",
    imageKey: "crud",
    icons: [ReactIcon, JavascriptIcon, ReduxIcon, HTMLIcon, CSSIcon],
  },
  {
    id: "DOG Soccer Team (CRUD React)",
    title: "DOG Soccer Team (CRUD React)",
    siteLink: "https://renato-doggyteam.netlify.app/",
    GhLink: "https://github.com/Netsujr/dog_soccer_team",
    path: "doggyteam",
    stack: "React",
    detailTitle: "Doggy Team |  ",
    translationKey: "doggyteam",
    imageKey: "doggy",
    icons: [ReactIcon, JavascriptIcon, ReduxIcon, HTMLIcon, CSSIcon],
  },
  {
    id: "Travel Advisor",
    title: "Travel Advisor",
    siteLink: "https://renato-travel-advisor.netlify.app/",
    GhLink: "https://github.com/Netsujr/Renato_Travel_App",
    path: "travel",
    stack: "React",
    detailTitle: "Travel Advisor |  ",
    translationKey: "travel",
    imageKey: "travel",
    icons: [
      JavascriptIcon,
      ReactIcon,
      HTMLIcon,
      CSSIcon,
      GoogleCloudIcon,
      RapidAPIIcon,
    ],
  },
  {
    id: "Whatsapp Clone",
    title: "Whatsapp Clone",
    GhLink: "https://github.com/Netsujr/whatsapp-clone",
    path: "whatsapp",
    stack: "React",
    offline: true,
    detailTitle: "WhatsApp Clone |  ",
    translationKey: "whatsapp",
    imageKey: "whatsapp",
    icons: [
      ReactIcon,
      JavascriptIcon,
      FirebaseIcon,
      HTMLIcon,
      CSSIcon,
      ReduxIcon,
    ],
  },
  {
    id: "Spotify Clone",
    title: "Spotify Clone",
    GhLink: "https://github.com/Netsujr/spotify-clone",
    path: "spotify",
    offline: true,
    stack: "React",
    detailTitle: "Spotify Clone |  ",
    translationKey: "spotify",
    imageKey: "spotify",
    icons: [ReactIcon, JavascriptIcon, FirebaseIcon, HTMLIcon, CSSIcon],
  },
  {
    id: "Disney+ Clone",
    title: "Disney+ Clone",
    GhLink: "https://github.com/Netsujr/disney-clone",
    path: "disney",
    offline: true,
    stack: "React",
    detailTitle: "Disney Clone |  ",
    translationKey: "disney",
    imageKey: "disney",
    icons: [
      ReactIcon,
      JavascriptIcon,
      ReduxIcon,
      HTMLIcon,
      CSSIcon,
      FirebaseIcon,
    ],
  },
  {
    id: "Tesla Clone",
    title: "Tesla Clone",
    GhLink: "https://github.com/Netsujr/tesla_clone_using_react",
    path: "tesla",
    offline: true,
    stack: "React",
    detailTitle: "Tesla Clone |  ",
    translationKey: "tesla",
    imageKey: "tesla",
    icons: [ReactIcon, JavascriptIcon, ReduxIcon, HTMLIcon, CSSIcon],
  },
  {
    id: "Zoom Clone (React Native)",
    title: "Zoom Clone (React Native)",
    GhLink: "https://github.com/Netsujr/zoom-clone",
    path: "zoom",
    offline: true,
    stack: "React",
    detailTitle: "Zoom Clone |  ",
    translationKey: "zoom",
    imageKey: "zoom",
    icons: [ReactIcon, JavascriptIcon, ReduxIcon, HTMLIcon, CSSIcon],
  },
  {
    id: "Artistable",
    title: "Artistable",
    GhLink: "https://github.com/Netsujr/artistible",
    path: "art",
    offline: true,
    stack: "Ruby",
    detailTitle: "Artistible |  ",
    translationKey: "art",
    imageKey: "artistible",
    icons: [JavascriptIcon, HTMLIcon, CSSIcon, RubyIcon, RailsIcon],
    cardIcons: [RubyIcon, RailsIcon],
  },
  {
    id: "Movie List",
    title: "Movie List",
    GhLink: "https://github.com/Netsujr/rails-watch-list",
    path: "movie",
    offline: true,
    stack: "Ruby",
    detailTitle: "MovieList |  ",
    translationKey: "movie",
    imageKey: "movie",
    icons: [HTMLIcon, CSSIcon, RubyIcon, RailsIcon],
    cardIcons: [RubyIcon, RailsIcon],
  },
];

// Process project data to add images based on type and imageKey
// and merge metadata (dateCreated, commitCount) from projectMetadata.json
const processedProjectData = projectData.map((project) => {
  const category = project.type === "pro" ? "pro" : "personal";
  const images = getProjectImages(category, project.imageKey);
  
  // Get metadata for this project by ID (project title)
  const metadata = projectMetadata[project.id || project.title] || {};
  
  // Remove any existing dateCreated/commitCount from project object
  const { dateCreated: _, commitCount: __, ...projectWithoutMetadata } = project;

  const dateCreated = metadata.dateCreated || null;
  
  return {
    ...projectWithoutMetadata,
    // Add ID if not present (use title as fallback)
    id: project.id || project.title,
    // Merge metadata from JSON file
    dateCreated,
    commitCount: metadata.commitCount !== undefined ? metadata.commitCount : null,
    // Automatically set isNew based on creation date (within last 90 days)
    isNew: isProjectNew(dateCreated),
    // Add images
    image: images.card,
    img1: images.detail[0],
    img2: images.detail[1],
    img3: images.detail[2],
  };
});

export default processedProjectData;
